import { format } from 'date-fns'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import Container from '@/components/atoms/container/container'
import ErrorNotification from '@/components/atoms/errorNotification/errorNotification'
import Footer from '@/components/organisms/footer/footer'
import Header from '@/components/organisms/header/header'
import VendorBonusOverlay from '@/components/organisms/vendorBonusOverlay/vendorBonusOverlay'
import VoucherScannerModal from '@/components/organisms/voucherScannerModal/voucherScannerModal'
import VoucherScanResultModal from '@/components/organisms/voucherScanResultModal/voucherScanResultModal'
import DepositSuccessAnimationTemplate, {
    DepositClaimStatus,
    DepositFlowStatus
} from '@/components/templates/depositSuccessAnimation/depositSuccessAnimationTemplate'
import DirectBettingConfirmationTemplate from '@/components/templates/directBettingConfirmation/directBettingConfirmationTemplate'
import PaymentComplete from '@/components/templates/paymentInProgress/components/paymentComplete'
import ReferralIntroTemplate from '@/components/templates/referralIntro/referralIntroTemplate'
import VoucherConfirmationTemplate from '@/components/templates/voucherConfirmation/voucherConfirmationTemplate'
import VoucherErrorTemplate from '@/components/templates/voucherDataError/VoucherErrorTemplate'
import { VoucherConfirmation } from '@/data/entities/voucher-confirmation'
import { VoucherPurchaseStep } from '@/data/enums/voucherPurchaseSteps'
import { useCheckInternetConnection } from '@/hooks'
import { useTranslate } from '@/i18n/useTranslate'
import { useOrder } from '@/providers'
import { apiService } from '@/services/apiService'
import { depositBonusService } from '@/services/depositBonusService'
import {
    getDepositRewardJob,
    PendingDepositRewardJob,
    settleDepositRewardJob
} from '@/services/depositRewardService'
import { getReferralEnabled, getVendorBonusEnabled } from '@/services/deviceConfigurationService'
import { POSService } from '@/services/posService'
import { referralService } from '@/services/referralService'
import { TransactionService } from '@/services/transactionService'
import { voucherCompletionService } from '@/services/voucherCompletionService'
import { VoucherResponse } from '@/types/pos/deposit'
import {
    DepositBonusEligibilityResponse,
    DepositBonusPromotion
} from '@/types/pos/depositBonus'
import { ReferralOutput } from '@/types/pos/referral'
import {
    getVendorBonusClaim,
    mapPromotionsByWalletId,
    shouldShowVendorBonusOverlay
} from '@/utilities/depositBonusHelper'
import { isValidPaymentRouteStep } from '@/utilities/navigationHelper'
import {
    activatePaymentSession,
    deactivatePaymentSession,
    getVoucherScanErrorType,
    onBuyVoucher,
    parseVoucherCodeFromScanResult,
    PaymentActivationError,
    printVoucher,
    VoucherScanErrorType
} from '@/utilities/paymentHelper'
import { PrintSlipRequest } from '@/utilities/posSlipHelper'
import {
    markReferralScreenShown,
    normalizePromoCode,
    shouldShowReferralEnrollmentScreen
} from '@/utilities/referralHelper'

import CardPayment from './components/cardPayment'
import InsertingCash from './components/insertingCash'

export default function PaymentInProgress() {
    const { t } = useTranslate()
    const { isOnline, setIsMoneyPending, deviceConfiguration } =
        useCheckInternetConnection()
    const referralEnabled = getReferralEnabled(deviceConfiguration)
    const vendorBonusEnabled = getVendorBonusEnabled(deviceConfiguration)
    const {
        setCurrentStep,
        setDirectBettingFailedAttempts,
        setCreatedVoucherData,
        setDirectBettingAccountId,
        setDirectBettingCompleted,
        setCashDetected,
        resetOrder,
        clearBasket,
        state
    } = useOrder()
    const sessionId = state.sessionId || undefined

    const selectedProduct = useMemo(() => {
        const basketItems = Object.values(state.basket)
        return basketItems.length > 0 ? basketItems[0].product : null
    }, [state.basket])

    const [amount, _setAmount] = useState<number>(0)
    const [amountByDraftDepositType, setAmountByDraftDepositType] = useState<
        Record<string, number> | undefined
    >(undefined)
    const [error, setError] = useState<PaymentActivationError | string | null>(
        null
    )
    const [voucherRecreateAttempts, setVoucherRecreateAttempts] =
        useState<number>(0)
    const [voucherData, setVoucherData] = useState<VoucherResponse | null>(
        state.createdVoucherData
    )
    const [isPrinted, setIsPrinted] = useState<boolean>(false)
    const [hasPrintingError, setHasPrintingError] = useState<boolean>(false)
    const [posSlip, setPosSlip] = useState<PrintSlipRequest | null>(null)
    const [printScreenMode, setPrintScreenMode] = useState<
        'processing' | 'printing'
    >('processing')
    const [printScreenVariant, setPrintScreenVariant] = useState<
        'voucher' | 'confirmation'
    >('voucher')
    const [printReturnStep, setPrintReturnStep] = useState<VoucherPurchaseStep>(
        VoucherPurchaseStep.VOUCHER_CONFIRMATION
    )
    const [isVoucherScannerOpen, setIsVoucherScannerOpen] =
        useState<boolean>(false)
    const [isVoucherScanResultOpen, setIsVoucherScanResultOpen] =
        useState<boolean>(false)
    const [voucherScanStatus, setVoucherScanStatus] = useState<
        'loading' | 'success' | 'error'
    >('loading')
    const [voucherScanErrorType, setVoucherScanErrorType] =
        useState<VoucherScanErrorType>('none')
    const [voucherScanSuccessAmount, setVoucherScanSuccessAmount] =
        useState<number>(0)
    const [directTransferStatus, setDirectTransferStatus] = useState<
        'idle' | 'success' | 'error'
    >(state.directBettingCompleted ? 'success' : 'idle')
    const [isDirectTransferSubmitting, setIsDirectTransferSubmitting] =
        useState<boolean>(false)
    const [referralIntro, setReferralIntro] = useState<ReferralOutput | null>(
        null
    )
    const [postSuccessStep, setPostSuccessStep] =
        useState<VoucherPurchaseStep>(VoucherPurchaseStep.PROCESS_PAYMENT)
    const [depositClaimStatus, setDepositClaimStatus] =
        useState<DepositClaimStatus>('none')
    const [depositClaimRewardAmount, setDepositClaimRewardAmount] =
        useState(0)
    const [referralEligibility, setReferralEligibility] = useState<
        'idle' | 'checking' | 'eligible' | 'ineligible' | 'unavailable'
    >('idle')
    const [pendingPromoCode, setPendingPromoCode] = useState('')
    const [vendorBonusPromotion, setVendorBonusPromotion] =
        useState<DepositBonusPromotion | null>(null)
    const [vendorBonusEligibility, setVendorBonusEligibility] =
        useState<DepositBonusEligibilityResponse | null>(null)
    const [isVendorBonusOverlayOpen, setIsVendorBonusOverlayOpen] =
        useState(false)
    const vendorBonusOverlayAttemptedRef = useRef(false)
    const pendingRewardJobRef = useRef<PendingDepositRewardJob | null>(null)
    const rewardJobStartedRef = useRef(false)
    const referralEligibilityCheckRef = useRef<{
        voucherCode: string
        walletId: number
        identifier: string
        state: 'eligible' | 'ineligible' | 'unavailable'
    } | null>(null)
    const [isSessionReady, setIsSessionReady] = useState<boolean>(false)
    const [deviceName, setDeviceName] = useState<string>('')
    const acceptorIntervalRef = useRef<number | null>(null)
    const latestAmountRef = useRef<number>(0)
    const lastPositiveDraftAmountRef = useRef<number>(0)
    const amountPollingLoopRef = useRef<number>(0)
    const navigate = useNavigate()
    const location = useLocation()
    const formatVoucherDate = (value?: string) =>
        value
            ? new Intl.DateTimeFormat('sr-Latn-RS', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
              }).format(new Date(value))
            : ''
    const formatVoucherTime = (value?: string) =>
        value ? format(new Date(value), 'HH:mm:ss') : ''
    const formatVoucherAmount = (amount?: number, currency?: string) => {
        const formattedAmount = new Intl.NumberFormat('sr-RS', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount ?? 0)
        return `${formattedAmount} ${currency ?? ''}`.trim()
    }
    const navigationState = location.state as
        | {
              bettingFlow?: string
              selectedBettingService?: string
              accountId?: string
              fromDirectBettingConfirmation?: boolean
              autoRetryDeposit?: boolean
              createdVoucherData?: VoucherResponse
          }
        | undefined

    useEffect(() => {
        if (voucherData) return
        const recovered =
            state.createdVoucherData ?? navigationState?.createdVoucherData
        if (recovered) {
            setVoucherData(recovered)
        }
    }, [
        navigationState?.createdVoucherData,
        state.createdVoucherData,
        voucherData
    ])

    useEffect(() => {
        if (
            navigationState?.accountId &&
            state.directBettingAccountId !== navigationState.accountId
        ) {
            setDirectBettingAccountId(navigationState.accountId)
        }
    }, [
        navigationState?.accountId,
        setDirectBettingAccountId,
        state.directBettingAccountId
    ])

    useEffect(() => {
        let isMounted = true

        const ensureCashierSession = async () => {
            try {
                await POSService.createSession()
                if (isMounted) {
                    setIsSessionReady(true)
                }
            } catch (sessionError) {
                console.error(
                    'Failed to ensure cashier session on payment page:',
                    sessionError
                )
                if (isMounted) {
                    setError('authRequired')
                }
            }
        }

        void ensureCashierSession()

        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        let isMounted = true

        const loadDeviceName = async () => {
            const credentials = await apiService.getCredentials(sessionId)
            if (isMounted && credentials?.device_name) {
                setDeviceName(credentials.device_name)
            }
        }

        void loadDeviceName()

        return () => {
            isMounted = false
        }
    }, [sessionId])

    const persistVoucherData = useCallback(
        (voucher: VoucherResponse | null) => {
            setVoucherData(voucher)
            setCreatedVoucherData(voucher)
        },
        [setCreatedVoucherData]
    )

    // Deterministically tear down the finished order so a completed
    // direct-deposit session can never bleed into the next customer. Clears
    // the in-memory context, the basket, and the localStorage mirror before
    // returning to the welcome screen.
    const finishAndResetToHome = useCallback(() => {
        persistVoucherData(null)
        setDirectBettingAccountId(null)
        resetOrder()
        clearBasket()
        try {
            window.localStorage.removeItem('vcash_order_state')
        } catch {
            // noop
        }
        // Replace so browser/Edge swipe-back cannot restore this payment route
        // after order state has already been cleared.
        navigate('/welcome-with-services', { replace: true })
    }, [
        persistVoucherData,
        setDirectBettingAccountId,
        resetOrder,
        clearBasket,
        navigate
    ])

    useEffect(() => {
        if (
            !referralEnabled &&
            state.currentStep === VoucherPurchaseStep.REFERRAL_INTRO
        ) {
            finishAndResetToHome()
        }
    }, [finishAndResetToHome, referralEnabled, state.currentStep])

    // History restore (e.g. Edge edge-swipe back) can remount this route after
    // welcome resetOrder() — UI would show cash via the default case while
    // bill-validator activate stays gated on INSERT_CASH.
    useEffect(() => {
        if (!isValidPaymentRouteStep(state.currentStep)) {
            navigate('/welcome-with-services', { replace: true })
        }
    }, [state.currentStep, navigate])

    const showDirectBettingInfoHint =
        state.voucherType === 'betting' &&
        navigationState?.bettingFlow === 'direct' &&
        Boolean(navigationState?.selectedBettingService) &&
        Boolean(navigationState?.accountId)

    const shouldShowDirectBettingConfirmation =
        state.voucherType === 'betting' &&
        navigationState?.bettingFlow === 'direct' &&
        Boolean(state.selectedBettingVendor) &&
        Boolean(navigationState?.accountId)

    const isDirectBettingDepositStep =
        shouldShowDirectBettingConfirmation &&
        (state.currentStep === VoucherPurchaseStep.PROCESS_PAYMENT ||
            state.currentStep ===
                VoucherPurchaseStep.DEPOSIT_SUCCESS_ANIMATION) &&
        !state.directBettingCompleted

    const isDirectBettingPayStep =
        (state.currentStep === VoucherPurchaseStep.INSERT_CASH ||
            state.currentStep === VoucherPurchaseStep.CARD_PAYMENT) &&
        shouldShowDirectBettingConfirmation &&
        !state.directBettingCompleted

    const vendorBonusAccountId =
        navigationState?.accountId ?? state.directBettingAccountId ?? ''
    const vendorBonusWalletId = state.selectedBettingVendor?.walletId ?? null

    const closeVendorBonusOverlay = useCallback(() => {
        setIsVendorBonusOverlayOpen(false)
    }, [])

    const vendorBonusClaim = useMemo(
        () =>
            getVendorBonusClaim(
                vendorBonusEligibility,
                state.selectedBettingVendor?.name ||
                    vendorBonusPromotion?.vendorName ||
                    '',
                state.selectedBettingVendor?.squareImageUrl ||
                    state.selectedBettingVendor?.sutImageUrl ||
                    state.selectedBettingVendor?.wideImageUrl
            ),
        [
            vendorBonusEligibility,
            vendorBonusPromotion?.vendorName,
            state.selectedBettingVendor?.name,
            state.selectedBettingVendor?.squareImageUrl,
            state.selectedBettingVendor?.sutImageUrl,
            state.selectedBettingVendor?.wideImageUrl
        ]
    )

    useEffect(() => {
        if (
            !vendorBonusEnabled ||
            !isDirectBettingPayStep ||
            vendorBonusWalletId == null ||
            !vendorBonusAccountId ||
            vendorBonusOverlayAttemptedRef.current
        ) {
            return
        }

        let cancelled = false

        void depositBonusService
            .getKioskPromotions(vendorBonusWalletId)
            .catch(() =>
                depositBonusService.getActivePromotions(vendorBonusWalletId)
            )
            .then(async (response) => {
                if (cancelled) return
                const promotion = mapPromotionsByWalletId(
                    response.promotions ?? []
                ).get(vendorBonusWalletId)
                if (!promotion?.steps?.length) {
                    vendorBonusOverlayAttemptedRef.current = true
                    return
                }

                const eligibility =
                    await depositBonusService.checkKioskEligibility(
                        vendorBonusWalletId,
                        vendorBonusAccountId
                    )
                if (cancelled) return
                vendorBonusOverlayAttemptedRef.current = true

                if (!shouldShowVendorBonusOverlay(eligibility)) return

                setVendorBonusPromotion(promotion)
                setVendorBonusEligibility(eligibility)
                setIsVendorBonusOverlayOpen(true)
            })
            .catch(() => {
                if (cancelled) return
                vendorBonusOverlayAttemptedRef.current = true
            })

        return () => {
            cancelled = true
        }
    }, [
        isDirectBettingPayStep,
        vendorBonusAccountId,
        vendorBonusEnabled,
        vendorBonusWalletId
    ])

    const shouldAutoPrintVoucherBeforeConfirmation =
        state.voucherType === 'gaming' ||
        (state.voucherType === 'betting' &&
            navigationState?.bettingFlow !== 'direct')

    const onChangeSelection = useCallback(() => {
        if (
            state.voucherType === 'betting' &&
            navigationState?.bettingFlow === 'direct'
        ) {
            navigate('/betting-service-selection', {
                state: {
                    voucherType: 'betting',
                    bettingFlow: 'direct',
                    selectedBettingService:
                        navigationState?.selectedBettingService ??
                        state.selectedBettingVendor?.slug,
                    accountId: navigationState?.accountId
                }
            })
            return
        }

        if (state.voucherType === 'gaming') {
            navigate('/gaming-selection', {
                state: { voucherType: 'gaming' }
            })
        }
    }, [
        navigate,
        navigationState?.accountId,
        navigationState?.bettingFlow,
        navigationState?.selectedBettingService,
        state.selectedBettingVendor?.slug,
        state.voucherType
    ])

    const onPrintVoucher = async (voucherPayload: VoucherResponse) => {
        const printResult = await printVoucher(
            voucherPayload,
            state.voucherType ?? '20',
            setError,
            sessionId,
            'terminal_voucher',
            { deviceName }
        )

        if (printResult.success) {
            setIsPrinted(true)
            setHasPrintingError(false)
            return
        }

        setIsPrinted(false)
        setHasPrintingError(true)
    }

    const onCompleteDirectBettingTransfer = useCallback(async () => {
        const walletId = state.selectedBettingVendor?.walletId
        const identifier = navigationState?.accountId
        const voucherCode = voucherData?.moneyTransfer?.voucherCode
        const transferAmount = voucherData?.moneyTransfer?.amount ?? 0

        if (state.directBettingCompleted) {
            setDirectTransferStatus('success')
            return
        }

        if (identifier) {
            lastAttemptedAccountIdRef.current = identifier
        }

        if (!walletId || !identifier || !voucherCode) {
            setDirectTransferStatus('error')
            setDirectBettingFailedAttempts(
                Math.min(state.directBettingFailedAttempts + 1, 3)
            )
            return
        }

        setDirectTransferStatus('idle')
        setIsDirectTransferSubmitting(true)
        try {
            const response = await voucherCompletionService.completeVoucher({
                walletId,
                identifier,
                voucherCode,
                amount: transferAmount
            })
            const referral = referralEnabled ? response.referral : undefined
            const rewardJob = getDepositRewardJob({
                response,
                walletId,
                identifier,
                vendorBonusEnabled,
                referralEnabled,
                promoCode: pendingPromoCode,
                vendorBonusClaim
            })

            setDirectBettingCompleted(true)
            setDirectTransferStatus('success')
            setDepositClaimRewardAmount(0)
            setDepositClaimStatus(rewardJob ? 'pending' : 'none')
            rewardJobStartedRef.current = false
            pendingRewardJobRef.current = rewardJob

            const shouldOfferEnrollment =
                referralEnabled &&
                Boolean(referral) &&
                shouldShowReferralEnrollmentScreen(referral!)

            if (shouldOfferEnrollment && referral) {
                markReferralScreenShown(referral.referralAccountId)
                setReferralIntro(referral)
                setPostSuccessStep(VoucherPurchaseStep.REFERRAL_INTRO)
            } else {
                setReferralIntro(null)
                setPostSuccessStep(VoucherPurchaseStep.PROCESS_PAYMENT)
            }

            setCurrentStep(VoucherPurchaseStep.DEPOSIT_SUCCESS_ANIMATION)
        } catch {
            setReferralIntro(null)
            setDirectTransferStatus('error')
            setDirectBettingFailedAttempts(
                Math.min(state.directBettingFailedAttempts + 1, 3)
            )
        } finally {
            setIsDirectTransferSubmitting(false)
        }
    }, [
        navigationState?.accountId,
        pendingPromoCode,
        referralEnabled,
        setCurrentStep,
        setDirectBettingCompleted,
        setDirectBettingFailedAttempts,
        state.directBettingCompleted,
        state.directBettingFailedAttempts,
        state.selectedBettingVendor?.walletId,
        vendorBonusClaim,
        vendorBonusEnabled,
        voucherData?.moneyTransfer?.amount,
        voucherData?.moneyTransfer?.voucherCode
    ])

    const settleDepositRewards = useCallback(async () => {
        if (rewardJobStartedRef.current) return
        const job = pendingRewardJobRef.current
        if (!job) return

        rewardJobStartedRef.current = true
        const rewardAmount = await settleDepositRewardJob(job)

        if (rewardAmount > 0) {
            setDepositClaimRewardAmount(rewardAmount)
            setDepositClaimStatus('success')
            return
        }

        setDepositClaimRewardAmount(0)
        setDepositClaimStatus('error')
    }, [])

    const hasAutoStartedDirectTransferRef = useRef(false)
    const lastAttemptedAccountIdRef = useRef<string | null>(null)
    const consumedAutoRetryKeyRef = useRef<string | null>(null)
    const awaitingPromoCode =
        referralEnabled &&
        isDirectBettingDepositStep &&
        referralEligibility === 'eligible' &&
        state.directBettingFailedAttempts === 0 &&
        directTransferStatus === 'idle' &&
        !isDirectTransferSubmitting

    const isDirectBettingProcessing =
        isDirectBettingDepositStep &&
        directTransferStatus === 'idle' &&
        (isDirectTransferSubmitting ||
            (referralEnabled && referralEligibility === 'checking') ||
            (state.directBettingFailedAttempts === 0 &&
                (!referralEnabled || referralEligibility !== 'eligible')))

    const depositFlowStatus: DepositFlowStatus =
        state.directBettingCompleted || directTransferStatus === 'success'
            ? 'success'
            : awaitingPromoCode
              ? 'promo'
              : directTransferStatus === 'error' ||
                  (state.directBettingFailedAttempts > 0 &&
                      !isDirectTransferSubmitting)
                ? 'failed'
                : 'processing'

    useEffect(() => {
        if (!isDirectBettingProcessing || awaitingPromoCode) {
            return
        }

        window.dispatchEvent(new Event('inactivity-pause'))

        return () => {
            window.dispatchEvent(new Event('inactivity-resume'))
        }
    }, [awaitingPromoCode, isDirectBettingProcessing])

    useEffect(() => {
        if (!isDirectBettingDepositStep) {
            return
        }

        if (!referralEnabled) {
            setReferralEligibility('ineligible')
            return
        }

        const walletId = state.selectedBettingVendor?.walletId
        const identifier = navigationState?.accountId
        const voucherCode = voucherData?.moneyTransfer?.voucherCode

        if (!walletId || !identifier || !voucherCode) {
            return
        }

        if (state.directBettingFailedAttempts > 0) {
            setReferralEligibility('ineligible')
            return
        }

        const cached = referralEligibilityCheckRef.current
        if (cached?.voucherCode === voucherCode) {
            if (
                cached.walletId === walletId &&
                cached.identifier === identifier
            ) {
                setReferralEligibility(cached.state)
                return
            }
            // Same voucher already checked for another identity — do not re-call.
            setReferralEligibility('unavailable')
            return
        }

        let cancelled = false
        setReferralEligibility('checking')

        void referralService
            .checkVoucherReferralEligibility({
                voucherCode,
                walletId,
                identifier
            })
            .then((response) => {
                if (cancelled) return
                const nextState = response.canClaimReferralCode
                    ? 'eligible'
                    : 'ineligible'
                referralEligibilityCheckRef.current = {
                    voucherCode,
                    walletId,
                    identifier,
                    state: nextState
                }
                setReferralEligibility(nextState)
            })
            .catch(() => {
                if (cancelled) return
                referralEligibilityCheckRef.current = {
                    voucherCode,
                    walletId,
                    identifier,
                    state: 'unavailable'
                }
                setReferralEligibility('unavailable')
            })

        return () => {
            cancelled = true
        }
    }, [
        isDirectBettingDepositStep,
        navigationState?.accountId,
        referralEnabled,
        state.directBettingFailedAttempts,
        state.selectedBettingVendor?.walletId,
        voucherData?.moneyTransfer?.voucherCode
    ])

    useEffect(() => {
        if (!isDirectBettingDepositStep) {
            hasAutoStartedDirectTransferRef.current = false
            return
        }

        if (
            state.directBettingFailedAttempts > 0 ||
            directTransferStatus !== 'idle' ||
            isDirectTransferSubmitting ||
            hasAutoStartedDirectTransferRef.current ||
            !voucherData?.moneyTransfer?.voucherCode ||
            (referralEnabled &&
                (referralEligibility === 'idle' ||
                    referralEligibility === 'checking' ||
                    referralEligibility === 'eligible'))
        ) {
            return
        }

        hasAutoStartedDirectTransferRef.current = true
        void onCompleteDirectBettingTransfer()
    }, [
        isDirectBettingDepositStep,
        state.directBettingFailedAttempts,
        directTransferStatus,
        isDirectTransferSubmitting,
        voucherData?.moneyTransfer?.voucherCode,
        referralEnabled,
        referralEligibility,
        onCompleteDirectBettingTransfer
    ])

    useEffect(() => {
        if (!isDirectBettingDepositStep || state.directBettingCompleted) {
            return
        }

        if (isDirectTransferSubmitting || directTransferStatus === 'success') {
            return
        }

        const currentId = navigationState?.accountId
        const voucherCode = voucherData?.moneyTransfer?.voucherCode
        if (!currentId || !voucherCode) {
            return
        }

        const retryKey = `${currentId}:${voucherCode}`
        const requestedRetry = navigationState?.autoRetryDeposit === true
        const idChanged =
            lastAttemptedAccountIdRef.current != null &&
            lastAttemptedAccountIdRef.current !== currentId

        if (!requestedRetry && !idChanged) {
            return
        }

        if (
            requestedRetry &&
            consumedAutoRetryKeyRef.current === retryKey &&
            !idChanged
        ) {
            return
        }

        consumedAutoRetryKeyRef.current = retryKey
        hasAutoStartedDirectTransferRef.current = true
        void onCompleteDirectBettingTransfer()
    }, [
        directTransferStatus,
        isDirectBettingDepositStep,
        isDirectTransferSubmitting,
        navigationState?.accountId,
        navigationState?.autoRetryDeposit,
        onCompleteDirectBettingTransfer,
        state.directBettingCompleted,
        voucherData?.moneyTransfer?.voucherCode
    ])

    const autoPrintVoucherAndShowOnScreen = useCallback(
        (createdVoucherData: VoucherResponse) => {
            setPrintScreenMode('printing')
            setPrintScreenVariant('voucher')
            setPrintReturnStep(VoucherPurchaseStep.VOUCHER_CONFIRMATION)
            setIsPrinted(false)
            setHasPrintingError(false)
            setCurrentStep(VoucherPurchaseStep.PRINT_VOUCHER)

            void printVoucher(
                createdVoucherData,
                state.voucherType ?? '20',
                setError,
                sessionId,
                'terminal_voucher',
                { deviceName }
            )
                .then((result) => {
                    if (result.success) {
                        setIsPrinted(true)
                        setHasPrintingError(false)
                    } else {
                        setIsPrinted(false)
                        setHasPrintingError(true)
                    }
                })
                .finally(() => {
                    // Regardless of print outcome, continue to on-screen voucher.
                    setCurrentStep(VoucherPurchaseStep.VOUCHER_CONFIRMATION)
                })
        },
        [deviceName, sessionId, setCurrentStep, state.voucherType]
    )

    const goToChangeAccountId = useCallback(() => {
        navigate('/betting-vendor-account', {
            state: {
                fromDirectBettingConfirmation: true,
                selectedBettingService:
                    navigationState?.selectedBettingService,
                accountId: navigationState?.accountId,
                createdVoucherData: voucherData
            }
        })
    }, [
        navigate,
        navigationState?.accountId,
        navigationState?.selectedBettingService,
        voucherData
    ])

    // Conversion clears the pending draft before the voucher response arrives.
    // Keep its last positive amount visible until the voucher supplies the total.
    const directBettingAmount =
        voucherData?.moneyTransfer?.amount ??
        (amount > 0 ? amount : lastPositiveDraftAmountRef.current)

    const renderDirectBettingFlow = () => (
        <DepositSuccessAnimationTemplate
            amount={directBettingAmount}
            currency={voucherData?.moneyTransfer?.currencyCode ?? 'RSD'}
            vendorName={state.selectedBettingVendor?.name ?? ''}
            vendorLogoUrl={state.selectedBettingVendor?.squareImageUrl}
            accountId={navigationState?.accountId ?? ''}
            failedAttempts={state.directBettingFailedAttempts}
            status={depositFlowStatus}
            isSubmitting={isDirectTransferSubmitting}
            promoCode={pendingPromoCode}
            onPromoCodeChange={(code) =>
                setPendingPromoCode(normalizePromoCode(code))
            }
            onRetry={() => {
                void onCompleteDirectBettingTransfer()
            }}
            onChangeAccountId={
                state.directBettingFailedAttempts >= 3
                    ? undefined
                    : goToChangeAccountId
            }
            onShowVoucher={() => {
                setCurrentStep(VoucherPurchaseStep.VOUCHER_CONFIRMATION)
            }}
            claimStatus={depositClaimStatus}
            claimRewardAmount={depositClaimRewardAmount}
            pendingRewardAmount={
                pendingRewardJobRef.current?.expectedRewardAmount
            }
            pendingRewardCurrency={
                pendingRewardJobRef.current?.expectedRewardCurrency
            }
            onBaseReady={() => {
                void settleDepositRewards()
            }}
            onComplete={() => {
                setCurrentStep(postSuccessStep)
            }}
        />
    )

    const renderStepContent = () => {
        switch (state.currentStep) {
            case VoucherPurchaseStep.PRINT_VOUCHER:
                return (
                    <PaymentComplete
                        mode={printScreenMode}
                        printingVariant={printScreenVariant}
                        navigate={navigate}
                        isPrintCompleted={isPrinted}
                        hasPrintingError={hasPrintingError}
                        onPrimaryButtonClick={() => {
                            setCurrentStep(printReturnStep)
                        }}
                    />
                )

            case VoucherPurchaseStep.VOUCHER_ERROR:
                return (
                    <VoucherErrorTemplate
                        navigate={navigate}
                        onTryAgain={() => {
                            setCurrentStep(VoucherPurchaseStep.PRINT_VOUCHER)
                            onBuyVoucher({
                                selectedVoucherType: state.voucherType,
                                bettingFlow: navigationState?.bettingFlow,
                                setVoucherRecreateAttempts,
                                voucherRecreateAttempts,
                                sessionId,
                                productId: selectedProduct?.id,
                                onError: (errorType) => {
                                    if (
                                        errorType ===
                                        'promoVoucherOnlyNotAllowed'
                                    ) {
                                        setCurrentStep(
                                            VoucherPurchaseStep.PROMO_VOUCHER_ERROR
                                        )
                                        return
                                    }
                                    setError(error)
                                    setCurrentStep(
                                        VoucherPurchaseStep.VOUCHER_ERROR
                                    )
                                    setVoucherRecreateAttempts(
                                        voucherRecreateAttempts + 1
                                    )
                                },
                                onSuccess: (voucherData) => {
                                    persistVoucherData(voucherData)
                                    setVoucherRecreateAttempts(0)
                                    setDirectBettingFailedAttempts(0)
                                    setDirectTransferStatus('idle')
                                    if (shouldShowDirectBettingConfirmation) {
                                        setCurrentStep(
                                            VoucherPurchaseStep.DEPOSIT_SUCCESS_ANIMATION
                                        )
                                        return
                                    }

                                    if (
                                        shouldAutoPrintVoucherBeforeConfirmation
                                    ) {
                                        autoPrintVoucherAndShowOnScreen(
                                            voucherData
                                        )
                                        return
                                    }

                                    setCurrentStep(
                                        VoucherPurchaseStep.VOUCHER_CONFIRMATION
                                    )
                                },
                                onPrintVoucher: () => {}
                            })
                        }}
                        voucherRecreateAttempts={voucherRecreateAttempts}
                    />
                )

            case VoucherPurchaseStep.PROMO_VOUCHER_ERROR:
                return (
                    <VoucherErrorTemplate
                        navigate={navigate}
                        onTryAgain={() => {
                            setCurrentStep(VoucherPurchaseStep.INSERT_CASH)
                        }}
                        voucherRecreateAttempts={0}
                        isPromoVoucherError={true}
                    />
                )

            case VoucherPurchaseStep.VOUCHER_CONFIRMATION:
                return (
                    <VoucherConfirmationTemplate
                        voucherConfirmation={
                            {
                                date:
                                    formatVoucherDate(
                                        voucherData?.moneyTransfer?.date
                                    ),
                                time:
                                    formatVoucherTime(
                                        voucherData?.moneyTransfer?.date
                                    ),
                                voucherCode:
                                    voucherData?.moneyTransfer?.voucherCode,
                                referenceNo:
                                    voucherData?.moneyTransfer
                                        ?.moneyTransferCode,
                                terminal:
                                    deviceName.trim() ||
                                    voucherData?.moneyTransfer?.venue?.name ||
                                    [
                                        voucherData?.moneyTransfer?.venue
                                            ?.address,
                                        voucherData?.moneyTransfer?.venue?.city
                                    ]
                                        .filter(Boolean)
                                        .join(', '),
                                amount: formatVoucherAmount(
                                    voucherData?.moneyTransfer?.amount,
                                    voucherData?.moneyTransfer?.currencyCode
                                ),
                                type: voucherData?.moneyTransfer?.typeCode,
                                amountNumber:
                                    voucherData?.moneyTransfer?.amount,
                                currency:
                                    voucherData?.moneyTransfer?.currencyCode,
                                usage: '???'
                            } as VoucherConfirmation
                        }
                        navigate={navigate}
                        posSlip={posSlip}
                        sessionId={sessionId}
                        canPrintVoucher={
                            Boolean(voucherData) &&
                            state.voucherType === 'betting' &&
                            navigationState?.bettingFlow === 'direct'
                        }
                        onPrintVoucher={() => {
                            if (!voucherData) {
                                return
                            }

                            setPrintScreenMode('printing')
                            setPrintScreenVariant('voucher')
                            setPrintReturnStep(
                                VoucherPurchaseStep.VOUCHER_CONFIRMATION
                            )
                            setIsPrinted(false)
                            setHasPrintingError(false)
                            setCurrentStep(VoucherPurchaseStep.PRINT_VOUCHER)
                            void onPrintVoucher(voucherData)
                        }}
                    />
                )
            case VoucherPurchaseStep.DEPOSIT_SUCCESS_ANIMATION:
                return renderDirectBettingFlow()
            case VoucherPurchaseStep.REFERRAL_INTRO:
                if (!referralEnabled || !referralIntro) {
                    return null
                }
                return (
                    <ReferralIntroTemplate
                        referral={referralIntro}
                        onFinish={finishAndResetToHome}
                    />
                )
            case VoucherPurchaseStep.PROCESS_PAYMENT:
                if (
                    !state.directBettingCompleted &&
                    directTransferStatus !== 'success'
                ) {
                    return renderDirectBettingFlow()
                }

                return (
                    <DirectBettingConfirmationTemplate
                        vendorName={state.selectedBettingVendor?.name ?? ''}
                        vendorLogoUrl={state.selectedBettingVendor?.squareImageUrl}
                        accountId={navigationState?.accountId ?? ''}
                        amount={directBettingAmount}
                        currency={voucherData?.moneyTransfer?.currencyCode ?? 'RSD'}
                        transferStatus={directTransferStatus}
                        failedAttempts={state.directBettingFailedAttempts}
                        isSubmitting={isDirectTransferSubmitting}
                        isCheckingEligibility={
                            referralEnabled &&
                            (referralEligibility === 'checking' ||
                                referralEligibility === 'idle')
                        }
                        awaitingPromoCode={awaitingPromoCode}
                        promoCode={pendingPromoCode}
                        onPromoCodeChange={(code) =>
                            setPendingPromoCode(normalizePromoCode(code))
                        }
                        onConfirm={() => {
                            void onCompleteDirectBettingTransfer()
                        }}
                        onChangeData={goToChangeAccountId}
                        onFinish={finishAndResetToHome}
                        onSessionTimeout={finishAndResetToHome}
                        onShowVoucher={() => {
                            setCurrentStep(
                                VoucherPurchaseStep.VOUCHER_CONFIRMATION
                            )
                        }}
                        onPrintConfirmation={() => {
                            if (!voucherData) {
                                return
                            }

                            setPrintScreenMode('printing')
                            setPrintScreenVariant('confirmation')
                            setPrintReturnStep(
                                VoucherPurchaseStep.PROCESS_PAYMENT
                            )
                            setIsPrinted(false)
                            setHasPrintingError(false)
                            setCurrentStep(VoucherPurchaseStep.PRINT_VOUCHER)
                            void printVoucher(
                                voucherData,
                                state.voucherType ?? '20',
                                setError,
                                sessionId,
                                'sut-direct-confirmation',
                                {
                                    deviceName,
                                    vendorName:
                                        state.selectedBettingVendor?.name ?? '',
                                    vendorUserId:
                                        navigationState?.accountId ?? ''
                                }
                            ).then((result) => {
                                if (result.success) {
                                    setIsPrinted(true)
                                    setHasPrintingError(false)
                                } else {
                                    setIsPrinted(false)
                                    setHasPrintingError(true)
                                }
                            })
                        }}
                        successDetails={{
                            date:
                                voucherData?.moneyTransfer?.date &&
                                format(
                                    new Date(voucherData.moneyTransfer.date),
                                    'dd.MM.yyyy'
                                ),
                            time:
                                voucherData?.moneyTransfer?.date &&
                                format(
                                    new Date(voucherData.moneyTransfer.date),
                                    'HH:mm'
                                ),
                            referenceNo:
                                voucherData?.moneyTransfer?.moneyTransferCode,
                            location: `${voucherData?.moneyTransfer?.venue?.address ?? ''}, ${voucherData?.moneyTransfer?.venue?.city ?? ''}`.replace(
                                /^,\s*|,\s*$/g,
                                ''
                            ),
                            amount:
                                voucherData?.moneyTransfer?.amount !== undefined
                                    ? `${voucherData.moneyTransfer.amount.toFixed(2)} ${voucherData?.moneyTransfer?.currencyCode ?? 'RSD'}`
                                    : undefined,
                            deviceName: deviceName || undefined
                        }}
                        posSlip={posSlip}
                        sessionId={sessionId}
                        bonusAmount={depositClaimRewardAmount}
                    />
                )

            case VoucherPurchaseStep.CARD_PAYMENT:
                return (
                    <CardPayment
                        accountId={navigationState?.accountId}
                        onChangeSelection={onChangeSelection}
                        canUsePreviousVoucher={state.voucherType === 'gaming'}
                        existingDraftAmount={amount}
                        vendorBonusClaim={vendorBonusClaim}
                        onUsePreviousVoucher={() => {
                            setIsVoucherScannerOpen(true)
                        }}
                        onPaymentSuccess={(response, slip) => {
                            console.log(
                                '✅ Card payment successful, proceeding to buy voucher',
                                response
                            )

                            setPosSlip(slip ?? null)
                            if (shouldShowDirectBettingConfirmation) {
                                setCurrentStep(
                                    VoucherPurchaseStep.DEPOSIT_SUCCESS_ANIMATION
                                )
                            } else {
                                setPrintScreenMode('processing')
                                setIsPrinted(false)
                                setHasPrintingError(false)
                                setCurrentStep(VoucherPurchaseStep.PRINT_VOUCHER)
                            }

                            onBuyVoucher({
                                selectedVoucherType: state.voucherType,
                                bettingFlow: navigationState?.bettingFlow,
                                setVoucherRecreateAttempts,
                                voucherRecreateAttempts,
                                sessionId,
                                productId: selectedProduct?.id,
                                onError: (errorType) => {
                                    if (
                                        errorType ===
                                        'promoVoucherOnlyNotAllowed'
                                    ) {
                                        setCurrentStep(
                                            VoucherPurchaseStep.PROMO_VOUCHER_ERROR
                                        )
                                        return
                                    }
                                    setError(errorType)
                                    setCurrentStep(
                                        VoucherPurchaseStep.VOUCHER_ERROR
                                    )
                                    setVoucherRecreateAttempts(
                                        voucherRecreateAttempts + 1
                                    )
                                },
                                onSuccess: (voucherData) => {
                                    persistVoucherData(voucherData)
                                    setVoucherRecreateAttempts(0)
                                    setDirectBettingFailedAttempts(0)
                                    setDirectTransferStatus('idle')
                                    if (shouldShowDirectBettingConfirmation) {
                                        setCurrentStep(
                                            VoucherPurchaseStep.DEPOSIT_SUCCESS_ANIMATION
                                        )
                                        return
                                    }

                                    if (
                                        shouldAutoPrintVoucherBeforeConfirmation
                                    ) {
                                        autoPrintVoucherAndShowOnScreen(
                                            voucherData
                                        )
                                        return
                                    }

                                    setCurrentStep(
                                        VoucherPurchaseStep.VOUCHER_CONFIRMATION
                                    )
                                },
                                onPrintVoucher: () => {}
                            })
                        }}
                        onPaymentError={(errorMessage) => {
                            console.error(
                                '❌ Card payment error:',
                                errorMessage
                            )
                            setError(errorMessage)
                        }}
                        onSwitchToCash={() => {
                            setCurrentStep(VoucherPurchaseStep.INSERT_CASH)
                        }}
                        onCancelTransaction={() => {
                            navigate('/welcome-with-services', {
                                replace: true
                            })
                        }}
                    />
                )

            default:
                // Only INSERT_CASH should render the acceptor UI. Any other
                // unexpected step is redirected by the guard above.
                if (state.currentStep !== VoucherPurchaseStep.INSERT_CASH) {
                    return null
                }
                return (
                    <InsertingCash
                        canUsePreviousVoucher={state.voucherType === 'gaming'}
                        amount={amount || 0}
                        amountByDraftDepositType={amountByDraftDepositType}
                        showNextPageHint={showDirectBettingInfoHint}
                        accountId={navigationState?.accountId}
                        onChangeSelection={onChangeSelection}
                        vendorBonusClaim={vendorBonusClaim}
                        onProcessPayment={() => {
                            if (shouldShowDirectBettingConfirmation) {
                                setCurrentStep(
                                    VoucherPurchaseStep.DEPOSIT_SUCCESS_ANIMATION
                                )
                            } else {
                                setPrintScreenMode('processing')
                                setIsPrinted(false)
                                setHasPrintingError(false)
                                setCurrentStep(VoucherPurchaseStep.PRINT_VOUCHER)
                            }
                            // call to buyVoucher
                            onBuyVoucher({
                                selectedVoucherType: state.voucherType,
                                bettingFlow: navigationState?.bettingFlow,
                                setVoucherRecreateAttempts,
                                voucherRecreateAttempts,
                                sessionId,
                                productId: selectedProduct?.id,
                                onError: (errorType) => {
                                    if (
                                        errorType ===
                                        'promoVoucherOnlyNotAllowed'
                                    ) {
                                        setCurrentStep(
                                            VoucherPurchaseStep.PROMO_VOUCHER_ERROR
                                        )
                                        return
                                    }
                                    setError(error)
                                    setCurrentStep(
                                        VoucherPurchaseStep.VOUCHER_ERROR
                                    )
                                    setVoucherRecreateAttempts(
                                        voucherRecreateAttempts + 1
                                    )
                                },
                                onSuccess: (voucherData) => {
                                    persistVoucherData(voucherData)
                                    setVoucherRecreateAttempts(0)
                                    setDirectBettingFailedAttempts(0)
                                    setDirectTransferStatus('idle')
                                    if (shouldShowDirectBettingConfirmation) {
                                        setCurrentStep(
                                            VoucherPurchaseStep.DEPOSIT_SUCCESS_ANIMATION
                                        )
                                        return
                                    }

                                    if (
                                        shouldAutoPrintVoucherBeforeConfirmation
                                    ) {
                                        autoPrintVoucherAndShowOnScreen(
                                            voucherData
                                        )
                                        return
                                    }

                                    setCurrentStep(
                                        VoucherPurchaseStep.VOUCHER_CONFIRMATION
                                    )
                                },
                                onPrintVoucher: () => {}
                            })
                        }}
                        onUsePreviousVoucher={() => {
                            setIsVoucherScannerOpen(true)
                        }}
                    />
                )
        }
    }

    useEffect(() => {
        latestAmountRef.current = amount
    }, [amount])

    // Keep a single long-poll loop active and stop it on unmount/screen change.
    useEffect(() => {
        if (!isSessionReady) {
            return
        }

        let isActive = true
        const loopId = ++amountPollingLoopRef.current
        let stateToken: string | undefined
        let retryAttempt = 0
        const retryBackoffMs = [1000, 2000, 5000]

        const wait = (ms: number) =>
            new Promise((resolve) => {
                setTimeout(resolve, ms)
            })

        const applyAmountUpdate = (
            nextAmount: number,
            nextByDraftDepositType?: Record<string, number>
        ) => {
            if (nextAmount > 0) {
                lastPositiveDraftAmountRef.current = nextAmount
                setIsMoneyPending(true)
                setCashDetected(true)
            } else {
                setIsMoneyPending(false)
            }

            if (nextAmount > latestAmountRef.current) {
                console.log(
                    'Dispatching money-added event to reset inactivity timer'
                )
                window.dispatchEvent(new Event('money-added'))
            }

            latestAmountRef.current = nextAmount
            _setAmount(nextAmount)
            setAmountByDraftDepositType(nextByDraftDepositType)
        }

        const startLongPolling = async () => {
            while (isActive && amountPollingLoopRef.current === loopId) {
                try {
                    const response =
                        await TransactionService.GetVoucherAmountChanges(
                            stateToken
                        )

                    if (!isActive || amountPollingLoopRef.current !== loopId) {
                        return
                    }

                    if (typeof response?.amount === 'number') {
                        applyAmountUpdate(
                            response.amount,
                            response.amountByDraftDepositType
                        )
                        stateToken = response.stateToken ?? stateToken
                    }

                    retryAttempt = 0
                } catch (error) {
                    console.error(
                        'Error long-polling voucher amount changes:',
                        error
                    )
                    const backoffIndex = Math.min(
                        retryAttempt,
                        retryBackoffMs.length - 1
                    )
                    retryAttempt += 1
                    await wait(retryBackoffMs[backoffIndex])
                }
            }
        }

        void startLongPolling()

        return () => {
            isActive = false
            amountPollingLoopRef.current += 1
        }
    }, [isSessionReady, setCashDetected, setIsMoneyPending])

    useEffect(() => {
        if (!isSessionReady) {
            return
        }

        if (!isOnline) {
            if (acceptorIntervalRef.current) {
                clearInterval(acceptorIntervalRef.current)
                acceptorIntervalRef.current = null
                deactivatePaymentSession(sessionId)
            }
            return
        }

        const isOnInsertCashStep =
            state.currentStep === VoucherPurchaseStep.INSERT_CASH &&
            !isVendorBonusOverlayOpen

        if (!isOnInsertCashStep) {
            if (acceptorIntervalRef.current) {
                clearInterval(acceptorIntervalRef.current)
                acceptorIntervalRef.current = null
                deactivatePaymentSession(sessionId)
            }
            return
        } else {
            if (acceptorIntervalRef.current == null && isOnline) {
                activatePaymentSession(
                    state.voucherType ?? '',
                    (activationError) => {
                        setError(activationError)
                    },
                    sessionId
                )
                acceptorIntervalRef.current = window.setInterval(async () => {
                    if (!acceptorIntervalRef.current) {
                        return
                    }
                    await activatePaymentSession(
                        state.voucherType ?? '',
                        (activationError) => {
                            setError(activationError)
                        },
                        sessionId
                    )
                }, 5000)
            }
        }

        return () => {
            if (acceptorIntervalRef.current) {
                clearInterval(acceptorIntervalRef.current)
                acceptorIntervalRef.current = null
            }
            if (isOnInsertCashStep) {
                deactivatePaymentSession(sessionId)
            }
        }
    }, [isSessionReady, state.currentStep, state.voucherType, isOnline, sessionId, isVendorBonusOverlayOpen])

    useEffect(() => () => setIsMoneyPending(false))

    const onScan = useCallback(
        (value: string) => {
            console.log('🔍 DEBUG: Voucher scanned:', value)
            setIsVoucherScannerOpen(false)
            setIsVoucherScanResultOpen(true)
            setVoucherScanStatus('loading')
            const createDraftFromVoucher = async () => {
                try {
                    const voucherCode = parseVoucherCodeFromScanResult(value)
                    if (!voucherCode) {
                        setVoucherScanStatus('error')
                        setVoucherScanErrorType('invalid')
                        return
                    }

                    await new Promise((resolve) => setTimeout(resolve, 1000))
                    const response =
                        await TransactionService.CreateDraftFromVoucher({
                            voucherCode,
                            targetVoucherType:
                                state.voucherType === 'gaming'
                                    ? 'non_betting'
                                    : (state.voucherType ?? '')
                        })
                    setVoucherScanStatus('success')
                    setVoucherScanSuccessAmount(
                        response.deviceDraftDeposit?.amount ?? 0
                    )
                } catch (error) {
                    console.error('Error creating draft from voucher:', error)
                    setVoucherScanStatus('error')
                    setVoucherScanErrorType(getVoucherScanErrorType(error))
                }
            }
            createDraftFromVoucher()
        },
        [state.voucherType]
    )
    const onClose = useCallback(() => {
        setIsVoucherScannerOpen(false)
    }, [setIsVoucherScannerOpen])

    if (!isValidPaymentRouteStep(state.currentStep)) {
        return null
    }

    return (
        <>
            <ErrorNotification
                message={error ?? ''}
                isVisible={error !== null}
                onClose={() => setError(null)}
            />
            <Container isFullHeight={true}>
                {((depositFlowStatus === 'failed' &&
                    state.directBettingFailedAttempts < 3) ||
                    depositFlowStatus === 'promo') &&
                isDirectBettingDepositStep ? (
                    <Header
                        navigateBackUrl={'/betting-vendor-account'}
                        navigateBackState={{
                            fromDirectBettingConfirmation: true,
                            selectedBettingService:
                                navigationState?.selectedBettingService,
                            accountId: navigationState?.accountId,
                            createdVoucherData: voucherData
                        }}
                        navigationBackText={t('header.back')}
                    />
                ) : state.currentStep ===
                      VoucherPurchaseStep.DEPOSIT_SUCCESS_ANIMATION ||
                  state.currentStep ===
                      VoucherPurchaseStep.REFERRAL_INTRO ? (
                    <Header />
                ) : state.currentStep === VoucherPurchaseStep.CARD_PAYMENT ? (
                    <Header
                        navigateBackUrl={'/payment-method'}
                        navigateBackState={location.state}
                        navigationBackText={t('header.back')}
                    />
                ) : amount === 0 &&
                  state.currentStep === VoucherPurchaseStep.INSERT_CASH ? (
                    <Header
                        navigateBackUrl={'/welcome-with-services'}
                        navigationBackText={' '}
                    />
                ) : (
                    <Header />
                )}

                <div className="payment-in-progress__content">
                    {renderStepContent()}
                </div>
                <Footer />
            </Container>
            <VoucherScannerModal
                isOpen={isVoucherScannerOpen}
                onScan={onScan}
                onClose={onClose}
            />
            <VoucherScanResultModal
                status={voucherScanStatus}
                successAmount={voucherScanSuccessAmount}
                errorType={voucherScanErrorType}
                isOpen={isVoucherScanResultOpen}
                onClose={() => setIsVoucherScanResultOpen(false)}
            />
            {isVendorBonusOverlayOpen &&
            vendorBonusPromotion &&
            vendorBonusEligibility ? (
                <VendorBonusOverlay
                    promotion={vendorBonusPromotion}
                    eligibility={vendorBonusEligibility}
                    vendorName={
                        state.selectedBettingVendor?.name ||
                        vendorBonusPromotion.vendorName
                    }
                    vendorLogoUrl={
                        state.selectedBettingVendor?.squareImageUrl ||
                        state.selectedBettingVendor?.sutImageUrl ||
                        state.selectedBettingVendor?.wideImageUrl
                    }
                    onContinue={closeVendorBonusOverlay}
                />
            ) : null}
        </>
    )
}
